import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { NV_PATHS, NvAccessDialog, NvAlertDialog, NvButton, NvCtaBanner, NvErrorState, NvTabItem } from "@/components/navigator";
import { useAccessGate } from "@/hooks/use-access-gate";
import { useRequest } from "@/hooks/use-request";
import { accessDenialOf } from "@/lib/access";
import { getApiError, isNotFoundError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { getAgentDetail } from "@/services/navigator/agent-detail-service";
import { contactPath } from "@/services/navigator/contact-service";
import { recordEvent, setMark, type MarkKind } from "@/services/navigator/finder-service";

import { ClassificationBar, DefinitionFormatDialog, ExternalHeader, type HeaderActions } from "./agent-detail-header";
import { InternalDetail } from "./agent-internal-detail";
import { DETAIL_GUTTER } from "./detail-parts";
import { AdoptionSection } from "./section-adoption";
import { ComparisonSection } from "./section-comparison";
import { FeaturesSection } from "./section-features";
import { MetricsSection } from "./section-metrics";
import { OverviewSection } from "./section-overview";
import { SupplierSection } from "./section-supplier";
import { TrustSection } from "./section-trust";
import { UsecasesSection } from "./section-usecases";
import { WorkflowSection } from "./section-workflow";

// F01-1 에이전트 상세 (설계서 Front v0.5 slide 39~58 · Figma ver.6 03-01~05). 수요자 · PwC 내부(공급자는 자사 제품만 — 아래 참고)가 열람하고,
// 서버가 제출 단계(2차까지 / 1차+WF / 1차만)와 회원 유형에 맞게 거른 값을 주면 화면은 값이 있는 섹션 · 바로가기 탭만 그려요.
// 섹션 순서: 개요 → 주요 기능 → 검증 지표 → 보안과 신뢰 → 워크플로우 → 유즈케이스 → 도입 조건 → 공급사 정보 → 유사 에이전트 비교 → 도입 검토 문의 (10/12 정의서 0.2.2).

type AnchorKey = "overview" | "features" | "metrics" | "trust" | "workflow" | "usecases" | "adoption" | "supplier" | "comparison";

// GNB(--nv-gnb-height 72)가 맨 위에 붙어 있고, 바로가기 바(약 53)는 그 아래에 붙어요. 스크롤 계산에 두 높이를 더해요.
const GNB_HEIGHT = 72;
const ANCHOR_BAR_HEIGHT = 53;

const ACCESS_LOGIN_MESSAGE = "에이전트 상세는\n로그인 후 이용할 수 있습니다.";
const ACCESS_FORBIDDEN_MESSAGE = "에이전트 상세는\n수요자 · PwC 내부 회원만 이용할 수 있습니다.";

// 비공개(Confidential) 에이전트: 서버 메시지 + Agent Owner 안내 문구, 아니면 null
function confidentialMessageOf(err: unknown) {
  const error = getApiError(err);
  if (error?.code !== "CONFIDENTIAL_AGENT") return null;
  const owner = (error.details?.[0] ?? {}) as unknown as { owner_name?: string; owner_org?: string };
  const who = owner.owner_name ? `\nAgent Owner: ${owner.owner_name}${owner.owner_org ? ` (${owner.owner_org})` : ""}` : "";
  return `${error.message}${who}`;
}

export function NavigatorAgentDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const gate = useAccessGate();
  const [alert, setAlert] = useState<string | null>(null);
  const [definitionOpen, setDefinitionOpen] = useState(false);
  const [active, setActive] = useState<AnchorKey | null>(null);

  // One request per (agent, entry source). Moving to another agent aborts this one and a late response of the
  // previous agent is ignored, so the page can never show agent A under agent B's address.
  const source = (location.state as { source?: string } | null)?.source ?? "direct";
  const { data: detail, error, reload, setData: setDetail } = useRequest((signal) => getAgentDetail(id, source, signal), [id, source]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id, location.state]);

  // Why it did not load: access refused (popup) · confidential (popup) · does not exist (4xx) · could not load (5xx / network, retry)
  const denial = accessDenialOf(error);
  const confidential = confidentialMessageOf(error);
  const missing = !!error && !denial && confidential === null && isNotFoundError(error);
  const failed = !!error && !denial && confidential === null && !missing;

  const anchors = useMemo(() => {
    if (!detail) return [];
    const s = detail.sections;
    const list: { key: AnchorKey; label: string }[] = [];
    if (s.overview) list.push({ key: "overview", label: "개요" });
    if (s.features) list.push({ key: "features", label: "주요 기능" });
    if (s.metrics) list.push({ key: "metrics", label: "검증 지표" });
    if (s.trust) list.push({ key: "trust", label: "보안과 신뢰" });
    if (s.workflow) list.push({ key: "workflow", label: s.workflow.title });
    if (s.usecases) list.push({ key: "usecases", label: "유즈케이스" });
    if (s.adoption) list.push({ key: "adoption", label: "도입 조건" });
    if (detail.supplier) list.push({ key: "supplier", label: "공급사 정보" });
    if (s.comparison) list.push({ key: "comparison", label: "비교" });
    return list;
  }, [detail]);

  // 스크롤 위치에 따라 현재 섹션 강조 (설계서 slide 40 · 3)
  useEffect(() => {
    if (!anchors.length) return;
    const onScroll = () => {
      let current: AnchorKey = anchors[0].key;
      for (const a of anchors) {
        const el = document.getElementById(`agent-${a.key}`);
        if (el && el.getBoundingClientRect().top <= GNB_HEIGHT + ANCHOR_BAR_HEIGHT + 120) current = a.key;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [anchors]);

  if (!detail) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-40">
        {missing && (
          <>
            <p className="text-[15px] text-nv-text-secondary">에이전트를 찾을 수 없습니다.</p>
            <Link to={NV_PATHS.agentFinder} className="text-[15px] text-nv-brand-text underline">
              Agent Finder로
            </Link>
          </>
        )}
        {failed && <NvErrorState error={error} onRetry={reload} />}
        <NvAccessDialog
          {...gate.dialogProps}
          open={denial !== null}
          reason={denial}
          onOpenChange={(open) => !open && navigate(NV_PATHS.agentFinder, { replace: true })}
          loginMessage={ACCESS_LOGIN_MESSAGE}
          forbiddenMessage={ACCESS_FORBIDDEN_MESSAGE}
        />
        <NvAlertDialog
          open={confidential !== null}
          onOpenChange={(open) => !open && navigate(NV_PATHS.agentFinder, { replace: true })}
          message={confidential ?? ""}
          actions={<NvButton onClick={() => navigate(NV_PATHS.agentFinder, { replace: true })}>확인</NvButton>}
        />
      </div>
    );
  }

  const toggleMark = async (kind: MarkKind) => {
    const field = kind === "bookmarks" ? "is_bookmarked" : "in_compare";
    const next = !detail[field];
    setDetail((d) => d && { ...d, [field]: next });
    try {
      await setMark(kind, detail.id, next);
    } catch (err) {
      setDetail((d) => d && { ...d, [field]: !next });
      setAlert(getApiError(err)?.message ?? "잠시 후 다시 시도해 주세요.");
    }
  };

  const actions: HeaderActions = {
    inquiryPath: contactPath({ source: "agent_detail", agent_id: detail.id }),
    onInquiry: () => recordEvent("inquiry_click", detail.id),
    onBookmark: () => toggleMark("bookmarks"),
    onCompare: () => toggleMark("compare"),
    onDefinition: () => setDefinitionOpen(true),
  };

  const dialogs = (
    <>
      <DefinitionFormatDialog agentId={detail.id} open={definitionOpen} onOpenChange={setDefinitionOpen} onError={setAlert} />
      <NvAlertDialog open={alert !== null} onOpenChange={(open) => !open && setAlert(null)} message={alert ?? ""} actions={<NvButton onClick={() => setAlert(null)}>확인</NvButton>} />
    </>
  );

  if (detail.detail_type === "internal") {
    return (
      <>
        <InternalDetail detail={detail} actions={actions} />
        {dialogs}
      </>
    );
  }

  const s = detail.sections;
  const scrollTo = (key: AnchorKey) => {
    const el = document.getElementById(`agent-${key}`);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - (GNB_HEIGHT + ANCHOR_BAR_HEIGHT), behavior: "smooth" });
  };
  const firstKey = anchors[0]?.key;

  return (
    <div className="pb-0">
      <ExternalHeader detail={detail} actions={actions} />
      <ClassificationBar detail={detail} />

      {anchors.length > 0 && (
        // 값 있는 섹션만 탭, 스크롤 시 상단 고정 + 현재 섹션 주황 밑줄, 탭 폭은 내용 기준
        <div id="agent-anchor-bar" className="sticky top-[var(--nv-gnb-height)] z-30 border-b border-nv-border bg-white/95 backdrop-blur-sm">
          <nav aria-label="섹션 바로가기" className={cn(DETAIL_GUTTER, "flex gap-8 overflow-x-auto")}>
            {anchors.map((a) => (
              <NvTabItem key={a.key} role="link" active={active === a.key} onClick={() => scrollTo(a.key)} className={cn("-mx-1 px-3 text-[14px] leading-[22px]", active !== a.key && "text-nv-text-secondary")}>
                {a.label}
              </NvTabItem>
            ))}
          </nav>
        </div>
      )}

      {s.overview && <OverviewSection data={s.overview} first={firstKey === "overview"} />}
      {s.features && <FeaturesSection data={s.features} />}
      {s.metrics && <MetricsSection data={s.metrics} />}
      {s.trust && <TrustSection data={s.trust} tight={s.features?.kind === "differentiators"} />}
      {s.workflow && <WorkflowSection data={s.workflow} agentName={detail.name} />}
      {s.usecases && <UsecasesSection data={s.usecases} />}
      {s.adoption && <AdoptionSection data={s.adoption} agentId={detail.id} />}
      {detail.supplier && <SupplierSection supplier={detail.supplier} agentId={detail.id} />}
      {s.comparison && <ComparisonSection data={s.comparison} />}

      {detail.audience !== "supplier" && (
        // 도입 검토 문의: 공급자에게는 표기하지 않아요. 주황 채움 버튼은 헤더와 이 배너 두 곳뿐.
        <section className="mx-auto max-w-[1440px] px-5 pb-24 pt-14 xl:px-[120px] xl:pt-16">
          <NvCtaBanner
            title="도입 검토를 시작해보세요"
            description="범위 산정, PoC 설계, 공급사 연결을 PwC가 지원합니다."
            actions={
              <>
                <NvButton asChild>
                  <Link to={actions.inquiryPath} onClick={actions.onInquiry}>
                    PwC와 함께 도입 문의하기
                  </Link>
                </NvButton>
                <NvButton variant="secondary" asChild>
                  <Link to={`/agents/${detail.id}/definition`}>상세 정의서 보기</Link>
                </NvButton>
              </>
            }
          />
        </section>
      )}
      {detail.audience === "supplier" && <div className="pb-24" />}
      {dialogs}
    </div>
  );
}
